import React, { useEffect, useState, useMemo } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import Cookies from 'js-cookie';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Link } from 'react-router-dom';
import './Analytics.css';

/* ── Custom tooltip component ── */
const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="analytics-tooltip">
            <div className="label">{label}</div>
            {payload.map((entry, i) => (
                <div key={i} className="value" style={{ color: entry.color || '#fff' }}>
                    {entry.name}: {formatNumber(Number(entry.value))}{suffix}
                </div>
            ))}
        </div>
    );
};

/* ── Number formatting ── */
const formatNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value : value.toFixed(2);
    }
    return value;
};


const HRProfile = () => {
  const baseUrl = API_BASE_URL;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Recent reviews state
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        setProfile(response.data);
      } catch (err) {
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [baseUrl]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!profile?.employee?.id) return;
      setAnalyticsLoading(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { employee: profile.employee.id }
        });
        setAnalytics(response.data);
      } catch (err) {
        setAnalytics(null);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    if (profile) {
      fetchAnalytics();
    }
  }, [baseUrl, profile]);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoadingReviews(true);
      setReviewsError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/users/me/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const completed = (Array.isArray(response.data.employee.goals) ? response.data.employee.goals : [])
          .filter(g => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
        completed.sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
        setReviews(completed);
      } catch (err) {
        setReviewsError('Failed to fetch recent reviews.');
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [baseUrl]);

  const stats = analytics?.overall_statistics || {};
  const timeliness = analytics?.overall_statistics || {};

  const quarterlyTrendsData = useMemo(() => {
    if (!analytics?.quarterly_trends) return [];
    return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
      quarter,
      ...stats
    }));
  }, [analytics]);

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Performance Overview</h4>
              <p className="text-muted mb-0 fs-13">Overview of your performance metrics and KPIs</p>
            </div>

            {loading ? (
              <div className="placeholder-glow">
                <span className="placeholder col-12 style-placeholder" style={{ width: '180px', height: '32px' }} />
              </div>
            ) : profile?.employee ? (
              <div className="bg-light border rounded px-3 py-2 text-end">
                <div className="fw-bold text-dark fs-15">
                  {profile?.employee?.first_name} {profile?.employee?.last_name}
                </div>
                <div className="text-primary fs-12 fw-semibold">
                  {profile?.employee?.employee_id ? `Code: ${profile.employee.employee_id}` : ''}
                </div>
                <div className="text-muted fs-12">
                  {profile?.employee?.position || 'HR Manager'}
                </div>
              </div>
            ) : null}
          </div>

          {/* Stats Grid */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="avatar avatar-lg bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                    <i className="ti ti-star fs-24" />
                  </div>
                  <div>
                    <span className="text-muted fs-13 d-block">Completed Goals</span>
                    <h4 className="fw-bold text-dark mb-0">
                      {analyticsLoading ? '...' : `${stats.completed_goals ?? 0} / ${stats.total_goals ?? 0}`}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="avatar avatar-lg bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center">
                    <i className="ti ti-trending-up fs-24" />
                  </div>
                  <div>
                    <span className="text-muted fs-13 d-block">Avg Performance</span>
                    <h4 className="fw-bold text-dark mb-0">
                      {analyticsLoading ? '...' : stats.average_progress ? `${stats.average_progress}%` : '0%'}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <span className="text-muted fs-13 d-block mb-2">Goal Completion Timeliness</span>
                  {analyticsLoading ? (
                    <span className="text-muted fs-13">Loading timeliness...</span>
                  ) : (
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      <span className="badge bg-success text-white px-2 py-1 fs-12">
                        On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                      </span>
                      <span className="badge bg-primary text-white px-2 py-1 fs-12">
                        Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                      </span>
                      <span className="badge bg-danger text-white px-2 py-1 fs-12">
                        Late: {timeliness.late_rate != null ? timeliness.late_rate : 0}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Chart Section */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light border-bottom py-3">
              <h6 className="card-title mb-0 fw-bold text-dark">
                Quarterly Performance Trends
              </h6>
            </div>
            <div className="card-body">
              {analyticsLoading ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="fs-13 mb-0">Loading trend chart...</p>
                </div>
              ) : (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                          <linearGradient id="areaGradientProfile" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.02} />
                          </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                      <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <YAxis 
                          domain={[0, 100]} 
                          tickFormatter={(v) => `${formatNumber(Number(v))}%`} 
                          tick={{ fontSize: 12, fill: '#6b7280' }} 
                      />
                      <Tooltip content={<CustomTooltip suffix="%" />} />
                      <Area
                        type="monotone"
                        dataKey="completion_rate"
                        name="Completion Rate"
                        stroke="#4F46E5"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#areaGradientProfile)"
                        dot={{ r: 5, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                        activeDot={{ r: 7, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Recent Reviews Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light border-bottom py-3 d-flex align-items-center justify-content-between">
              <h6 className="card-title mb-0 fw-bold text-dark">Recent Reviews</h6>
              <Link to="/hr/my-goal-reviews" className="btn btn-sm btn-primary">
                View All
              </Link>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Activity / KPI</th>
                    <th>Performance Standard / Target</th>
                    <th>Metric</th>
                    <th>Self Score</th>
                    <th>HOD Score</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingReviews ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                        Loading reviews...
                      </td>
                    </tr>
                  ) : reviewsError ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-danger fs-13">
                        {reviewsError}
                      </td>
                    </tr>
                  ) : (!reviews || reviews.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No recent reviews found.
                      </td>
                    </tr>
                  ) : (
                    reviews.slice(0, 5).map((review, idx) => (
                      <tr key={review.id || idx}>
                        <td className="ps-3 fw-semibold text-truncate" style={{ maxWidth: '220px' }}>
                          {review.activity || '-'}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: '300px' }}>
                          {review.performance_standard || '-'}
                        </td>
                        <td>{review.metric || '-'}</td>
                        <td className="text-primary fw-bold">
                          {review.score != null ? review.score : '-'}
                        </td>
                        <td className="fw-semibold text-dark">
                          {review.hod_score != null ? review.hod_score : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default HRProfile;
